import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { addDays } from 'date-fns';

import DatePickerField from '@shared/fields/DatePickerField';
import { DateTimeService } from '@shared/services/DateTimeService';
import {
  SearchContainer,
  SearchForm,
  FormControls,
  FormItem,
  Label,
  InputContainer,
  Input,
  SearchButton,
} from '../styles';
import CheckinIcon from '../../../../../assets/images/icons/checkin.svg';

import { AlgoliaService } from '../../../services/AlgoliaService';
import { PlaceSuggestionType, PointsRecoveryLocationType } from '../types/types';

const StyledIcon = styled.img<{
  position: 'left' | 'right';
}>`
  width: 24px;
  height: 24px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${({ position }) => (position === 'left' ? '10px' : 'auto')};
  right: ${({ position }) => (position === 'right' ? '10px' : 'auto')};
  fill: black;
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${({ position }) => (position === 'left' ? '40px' : '10px')};
  padding-right: ${({ position }) => (position === 'right' ? '40px' : '10px')};
`;

const StyletDatePickerField = styled(DatePickerField)`
  margin: 0;

  > input {
    width: 100%;
    height: 40px;
  }
`;

const SearchPoints = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);

  const placeInputRef = useRef(null);
  const AUTOCOMPLETE_SETTINGS = {
    hint: true,
    debug: true,
  };
  const generateLink = () => {
    if (coordinates) {
      return `/stage-recuperation-de-points/reservation?latitude=${coordinates.lat}&longitude=${
        coordinates.lng
      }&date=${DateTimeService.formattedDate(selectedDate, 'yyyy-MM-dd')}&title=${
        selectedPlace?.place_name || ''
      }`;
    }

    if (selectedPlace && 'slug' in selectedPlace) {
      return `/stage-recuperation-de-points/${selectedPlace.slug}`;
    }

    return '';
  };

  const redirectLink = generateLink();

  const onChangeDate = (date: Date | null) => {
    setSelectedDate(date);
  };

  const onChangePlace = (place: PointsRecoveryLocationType | PlaceSuggestionType | null) => {
    if (place && 'geometry' in place) {
      setCoordinates({ lat: place.geometry.coordinates[1], lng: place.geometry.coordinates[0] });
    } else {
      setCoordinates(null);
    }

    setSelectedPlace(place);
  };

  useEffect(() => {
    if (placeInputRef.current) {
      global
        .autocomplete(placeInputRef.current, AUTOCOMPLETE_SETTINGS, [
          AlgoliaService.generatePlacesDataset(),
          AlgoliaService.generatePointsDataset(),
        ])
        .on(
          'autocomplete:selected',
          (event, suggestion: PointsRecoveryLocationType | PlaceSuggestionType | null) => {
            onChangePlace(suggestion);
          }
        );
    }
  }, [placeInputRef]);

  return (
    <SearchContainer>
      <SearchForm>
        <FormControls>
          <FormItem>
            <Label htmlFor="place">Où souhaitez-vous faire un stage?</Label>
            <InputContainer>
              <StyledIcon src={CheckinIcon} alt="Checkin" position="left" />
              <StyledInput
                ref={placeInputRef}
                id="place"
                placeholder="Ville, arrondissement"
                type="text"
                name="place"
                autoFocus
                position="left"
              />
            </InputContainer>
          </FormItem>
          <FormItem>
            <Label htmlFor="place">Quelle date?</Label>

            <InputContainer>
              <StyletDatePickerField
                style={{ margin: 0, maxHeight: 45 }}
                name="date"
                placeholder={DateTimeService.setDateLocale(new Date(), 'dd MMMM yyyy')}
                onChange={(name: string, dateObj: Date) => onChangeDate(dateObj)}
                defaultDate={null}
                minDate={new Date()}
                maxDate={addDays(new Date(), 60)}
                dateFormat="dd MMMM yyyy"
                onBlur={() => {}}
              />
            </InputContainer>
          </FormItem>
          <SearchButton disabled={!selectedDate || !selectedPlace} href={redirectLink}>
            Rechercher
          </SearchButton>
        </FormControls>
      </SearchForm>
    </SearchContainer>
  );
};

export { SearchPoints };
